// wiring.ts — 데이터·API 배선(개발자 소유). 컨덕터는 이 파일이 없을 때 한 번 만들고 다시 건드리지 않는다.
//  화면 파일은 컨덕터가 재핸드오프 때 통째로 덮어쓴다 — 배선은 여기에만 둔다. 계약(Wiring)은 화면 파일이 낸다(문서 50).
//  최초 골격: pbo-conductor — 합배송스케쥴 (ma19)
//
//  이 화면의 배선 (TM-4166, 1.0 MA10 이관) — moms-be `/api/v1/pair-order-schedules` 만 부른다. 1.0 `/api/grid/*`·`/api/common/*` 는 쓰지 않는다.
//   · 조회: [조회]가 확정한 결제일 기간으로 GET. 기본 기간은 오늘 하루(1.0 MA10 이 MUSINSA 화주에게 주던 기본값).
//     확정한 조건은 메뉴 세션·URL 에 싣고(searchQuery.ts), 다른 메뉴를 다녀오면 URL 에서 복원해 다시 조회한다.
//   · 화주코드(SPR_NM): 1.0 메타의 필수 컬럼. [추가]·업로드 행은 로그인 화주로 채운다. 테이블에 컬럼이 없어 서버에 보내지 않고, 조회 행은 1.0 처럼 빈칸이다.
//     서버 상한 10,000건(1.0 그리드 페이지 크기)이라 한 번에 받고 표가 클라이언트 페이징한다.
//   · 행 상태: 서버 행 위에 [추가]·업로드로 넣은 신규 행(id 'new-n', __new)을 얹는다. 편집 값은 표 onRowsChange 로 받아 둔다.
//   · [저장]: 체크한 행(편집 반영본)을 신규·수정으로 갈라 한 번에 보낸다. 형식 검증은 서버와 같은 규칙으로 먼저 막는다.
//     저장·삭제 뒤 재조회해도 이번에 반영하지 않은 행의 편집은 남긴다. 그 행의 before 는 편집을 시작한 조회 값이다.
//   · [삭제]: 체크한 행 중 신규 행은 표에서만 빼고, 저장된 행은 deleteIds 로 바로 지운다(확인창 = 복구 불가 + 건수).
//   · 업로드: 파일 행을 신규 행으로 표에 더하고 그 행만 체크한다. 서버 반영은 [저장] 확인창으로(CR-06).
//   · 다운로드: 사유 모달 값으로 서버 xlsx(감사로그에 사유가 남는다). 조회한 기간 기준이다.
//   · 권한: 생성본의 권한 슬롯(permission, scripts/insert-permission-slot.mjs)으로 버튼을 숨기고 셀을 잠근다. 슬롯 값은 표 슬롯이 usePermissionSlot('ma19')로 채운다.
//     서버가 같은 판정(MA10·MA19 버튼 권한)으로 한 번 더 막는다.
//   · 미저장: 생성본은 셀 편집만 세고, 행 목록(ROWS)이 바뀌면 그 기준선을 비운다. 그래서 [추가]·업로드 행과 그 뒤에도 남는 셀 편집은 여기서 따로 센다.
//     메뉴 이동(useLeaveGuard)·[조회]·새로고침이 이 수를 보고 묻는다.
//   모듈 변수는 표 슬롯(ROWS)이 마운트 때 채우고 언마운트 때 비운다(⑤). 액션 슬롯은 이벤트라 훅을 못 쓰므로 이 변수를 읽는다(⑦).
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import dayjs from 'dayjs'
import { core } from '@musinsa/pbo-core'
import { useLeaveGuard } from '@/components/layout/leaveGuard'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { downloadPairOrderSchedulesExcel, savePairOrderSchedules } from '@/services/master/pairOrderSchedule/apis'
import {
  PAIR_ORDER_SCHEDULE_DOWNLOAD_DETAIL_MIN_LENGTH,
  PAIR_ORDER_SCHEDULE_EDIT_COLUMNS,
  PAIR_ORDER_SCHEDULE_MENU_CODE,
  PAIR_ORDER_SCHEDULE_UPLOAD_MAX_BYTES,
  PAIR_ORDER_SCHEDULE_UPLOAD_MAX_MB,
} from '@/services/master/pairOrderSchedule/consts'
import { apiErrorMessage, isNewRow, requireWithinSaveCap, toSaveRequest, toSearchReq } from '@/services/master/pairOrderSchedule/mapping'
import type { PairOrderScheduleRow, PairOrderScheduleSearchReq } from '@/services/master/pairOrderSchedule/types'
import { useGetPairOrderScheduleList } from '@/services/master/pairOrderSchedule/usePairOrderScheduleQueries'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { parsePairOrderScheduleExcel } from './excelImport'
import type { ActionCtx, Row, Wiring } from './page.generated'
import { readSearchQuery, toClsDateRange, toSearchQuery } from './searchQuery'
import { clearTableUnsaved, hasUnsavedEdits, revertUnsavedEdits, setTableUnsaved, UNSAVED_CONTENT, UNSAVED_TITLE } from './unsavedGuard'

/**
 * 표 id(생성본 columnLayout 키) → gridCd(docs/conventions/grid-code.md) — [추가] 프리셋만 읽는다(LOGISPRD-17312).
 * 툴바·별표는 생성본 permTool·permCols 가 표 id 를 안 받아 메뉴 단위 값 그대로다. MA19_HEAD 설정 행은 아직 복사 전이다.
 */
const TABLE_ID = 'ma19Table'
const GRID_CDS = { [TABLE_ID]: 'MA19_HEAD' } as const

/** 미저장 등록 키 — 생성본 표('ma19Table')와 따로 센다. */
const UNSAVED_KEY = 'ma19Rows'

/**
 * 저장·삭제 요청 진행 수 — wiring.useBusy 가 구독해 요청 중 [저장]·[삭제]를 잠근다(연타 방지).
 * 화면을 떠나면 세대(busyEpoch)를 올리고 0 으로 돌린다. 떠나기 전 요청은 끝나도 옛 세대라 새 화면의 수를 빼지 않는다.
 */
let busyCount = 0
let busyEpoch = 0
const busyListeners = new Set<() => void>()
const notifyBusy = () => busyListeners.forEach((l) => l())
const subscribeBusy = (listener: () => void) => {
  busyListeners.add(listener)
  return () => {
    busyListeners.delete(listener)
  }
}
const withBusy = async <T>(run: () => Promise<T>): Promise<T> => {
  const epoch = busyEpoch
  busyCount += 1
  notifyBusy()
  try {
    return await run()
  } finally {
    if (epoch === busyEpoch) busyCount -= 1
    notifyBusy()
  }
}
const resetBusy = () => {
  busyEpoch += 1
  busyCount = 0
  notifyBusy()
}

/** 로그인 화주 — 화주 전환을 반영한다(1.0 onAddRow 의 userInfo.company 와 같은 값). */
const loginShipperCode = (): string => {
  const user = AuthStorage.user.get()
  return user.shipperCode || user.SPR_NM || ''
}

const blankRow = (id: string): PairOrderScheduleRow => ({
  id,
  SPR_NM: loginShipperCode(),
  CLS_DATE: '',
  CUT_SEQ: '',
  CUT_OFF_TIME: '',
  IS_CURRENT: '',
  FST_GI_END: '',
  LST_GI_END: '',
  INS_DATE: '',
  INS_BY: '',
  UPD_DATE: '',
  UPD_BY: '',
  __new: true,
})

/** 표 슬롯이 마운트 동안 채우는 다리. 액션 슬롯(이벤트)이 읽는다. */
const bridge = {
  committed: null as PairOrderScheduleSearchReq | null,
  slot: null as ReturnType<typeof usePermissionSlot> | null,
  edited: new Map<string, Row>(),
  /** 마지막 조회 결과(서버 값) — 수정 행에서 저장된 값과 같은 칸은 형식 검증을 건너뛰고, before(조회 당시 값)로 보낸다. */
  stored: new Map<string, Row>(),
  addedCount: 0,
  newSeq: 0,
  search: (() => {}) as (params: PairOrderScheduleSearchReq) => void,
  /** 확정한 조회 조건·목록 검색어를 메뉴 세션·URL 에 싣는다. 표 슬롯이 마운트 동안 채운다. */
  persist: (() => {}) as (params: PairOrderScheduleSearchReq, keyword: string) => void,
  /** 저장·삭제 뒤 재조회. [doneIds] 는 이번에 반영한 행이다. 다른 행의 편집은 남긴다. */
  refetch: (() => {}) as (doneIds: Set<string>) => void,
  addRows: (() => {}) as (rows: Row[]) => void,
  removeAdded: (() => {}) as (ids: Set<string>) => void,
  /** 받아 둔 편집을 행 목록에 다시 접는다 — 목록 검색어가 바뀌어 표가 숨었던 행을 다시 그릴 때. */
  fold: () => {},
  /** 마지막으로 본 목록 검색어 — 바뀔 때만 접는다. */
  listQ: '',
  /** 미저장 버림 — 신규 행과 셀 편집을 모두 지운다. */
  discard: () => {},
}

const resetBridge = () => {
  bridge.committed = null
  bridge.slot = null
  bridge.edited = new Map()
  bridge.stored = new Map()
  bridge.addedCount = 0
  bridge.newSeq = 0
  bridge.search = () => {}
  bridge.persist = () => {}
  bridge.refetch = () => {}
  bridge.addRows = () => {}
  bridge.removeAdded = () => {}
  bridge.fold = () => {}
  bridge.listQ = ''
  bridge.discard = () => {}
  resetBusy()
  clearTableUnsaved(UNSAVED_KEY)
}

const nextNewId = () => {
  bridge.newSeq += 1
  return `new-${bridge.newSeq}`
}

/** 체크 행을 편집 반영본으로 — 표 onRowsChange 가 올린 값이 있으면 그것을 쓴다. */
const withEdits = (rows: Row[]): Row[] => rows.map((r) => bridge.edited.get(String(r.id)) ?? r)

const cell = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '')
/**
 * 화주코드(SPR_NM)도 센다 — 서버에 저장하지 않는 값이지만 [저장]이 필수로 요구하므로, 조회 행에 화주코드만 채운 뒤 떠나면
 * 다시 입력해야 한다. 그 편집도 미저장으로 보고 이동 전에 묻는다.
 */
const DIRTY_KEYS = ['SPR_NM', ...PAIR_ORDER_SCHEDULE_EDIT_COLUMNS.map(({ key }) => key)]
const isEditedRow = (row: Row): boolean => {
  const stored = bridge.stored.get(String(row.id))
  return !!stored && DIRTY_KEYS.some((key) => cell(row[key]) !== cell(stored[key]))
}

/** 미저장 수 = 신규 행 + 서버 값과 다른 수정 행. 생성본 기준선이 행 추가로 비워져도 이 수는 남는다. */
const syncUnsaved = () => {
  const edited = Array.from(bridge.edited.values()).filter((r) => !isNewRow(r) && isEditedRow(r)).length
  setTableUnsaved(UNSAVED_KEY, bridge.addedCount + edited, () => bridge.discard())
}

/** 메뉴 이동 전 확인 — 미저장이 없으면 바로 보낸다. [이동]이면 미저장을 버리고 보낸다. */
const confirmLeaveUnsaved = (): boolean | Promise<boolean> => {
  if (!hasUnsavedEdits()) return true
  return new Promise((resolve) => {
    core.Modal.confirm({
      centered: true,
      title: UNSAVED_TITLE,
      content: UNSAVED_CONTENT,
      okText: '이동',
      cancelText: '취소',
      okButtonProps: { danger: true },
      onOk: () => {
        revertUnsavedEdits()
        resolve(true)
      },
      onCancel: () => resolve(false),
    })
  })
}

/**
 * 서버 문구 없이 실패했을 때의 안내. 업무 거절은 서버가 meta.message 를 준다. 문구가 없으면 출고확정이 대표 행 잠금을 오래 쥐어
 * 저장이 대기 끝에 끊긴 경우가 대부분이다(moms-be 잠금 대기 초과는 공통 핸들러가 없어 문구 없는 500).
 */
const SAVE_FAILED = '저장하지 못했습니다. 출고확정이 진행 중이면 잠시 뒤 다시 저장해 주세요.'
const DELETE_FAILED = '삭제하지 못했습니다. 출고확정이 진행 중이면 잠시 뒤 다시 삭제해 주세요.'

/** 재조회 뒤에도 남길 편집 — 이번에 반영하지 않은 행 중 신규 행과 서버 값과 다른 수정 행. */
const keepPendingEdits = (doneIds: Set<string>) => {
  bridge.edited = new Map(Array.from(bridge.edited).filter(([id, r]) => !doneIds.has(id) && (isNewRow(r) || isEditedRow(r))))
}

/** 실패 안내는 토스트 하나로 — 옛 알럿(MAlert)·window.confirm 은 쓰지 않는다(MOMS-SR-01). throw 로 확인창·모달을 연 채 둔다. */
const fail = (error: unknown, fallback: string): never => {
  core.message.error(apiErrorMessage(error, fallback))
  throw error instanceof Error ? error : new Error(fallback)
}

/** 조회 기본 기간 — 오늘 하루. 1.0 MA10(gridsub DATE_MULTI)이 MUSINSA 화주에게 주던 [오늘, 오늘]과 같다. */
const defaultRange = (): [string, string] => {
  const today = dayjs().format('YYYY-MM-DD')
  return [today, today]
}

/**
 * 쿼리 키의 재조회 번호. 진입·[조회]·저장 뒤 재조회가 모두 이 하나의 카운터에서 받아, 다른 진입에서 쓴 키가 다시 나오지 않는다.
 * 키가 겹치면 staleTime Infinity 캐시의 옛 목록이 요청 없이 보이고, 그 값을 before 로 저장하면 서버가 '조회 뒤 바뀜'으로 거절한다.
 */
let nonceSeq = 0
const nextNonce = (): number => {
  nonceSeq += 1
  return nonceSeq
}

export const wiring: Wiring = {
  rows: {
    ROWS: () => {
      const slot = usePermissionSlot(PAIR_ORDER_SCHEDULE_MENU_CODE, GRID_CDS)
      // 다른 메뉴를 다녀오면 URL 에 남긴 마지막 조회 조건으로 다시 조회한다(조회영역 값은 filterInitial 이 같은 URL 로 복원).
      // URL 은 이 훅 말고도 filterInitial·stateInitial 이 읽는다. 둘은 생성본이 훅 밖에서 먼저 부르는 동기 슬롯이라 이 상태를 넘겨받을 수 없어
      // 같은 window.location.search 를 각자 파싱한다(searchQuery.readSearchQuery).
      const [restored] = useState(() => readSearchQuery())
      const [params, setParams] = useState<PairOrderScheduleSearchReq | null>(restored.req)
      const [nonce, setNonce] = useState(nextNonce)
      const { replaceQuery } = useParsedQuery()
      const menuSession = useMenuOnSession()
      const [added, setAdded] = useState<Row[]>([])
      const [foldTick, setFoldTick] = useState(0)
      const query = useGetPairOrderScheduleList(params, nonce)

      // 렌더 중 대입은 의도다 — 생성본은 이 훅을 부른 바로 뒤 같은 렌더에서 wiring.permission 을 읽는다.
      // effect 로 옮기면 권한이 늦게 와도 버튼·셀이 한 렌더 늦는다. 같은 값을 넣는 대입이라 두 번 렌더돼도 결과가 같다.
      bridge.slot = slot
      useLeaveGuard(confirmLeaveUnsaved)
      useEffect(() => {
        bridge.committed = restored.req
        bridge.listQ = restored.keyword
        bridge.persist = (next, keyword) => {
          const searchQuery = toSearchQuery(next, keyword)
          MenuStorage.menuOnSession.setSearch(searchQuery)
          menuSession.reload()
          replaceQuery(searchQuery)
        }
        bridge.search = (next) => {
          bridge.committed = next
          bridge.persist(next, '')
          bridge.listQ = '' // 생성본이 [조회] 뒤 목록 검색어를 비운다. onState 를 기다리지 않고 맞춘다
          bridge.edited = new Map()
          setAdded([])
          setParams(next)
          setNonce(nextNonce())
        }
        bridge.refetch = (doneIds) => {
          keepPendingEdits(doneIds)
          syncUnsaved()
          setNonce(nextNonce())
        }
        bridge.addRows = (rows) => setAdded((cur) => [...rows, ...cur])
        bridge.removeAdded = (ids) => {
          ids.forEach((id) => bridge.edited.delete(id))
          setAdded((cur) => cur.filter((r) => !ids.has(String(r.id))))
        }
        bridge.fold = () => setFoldTick((n) => n + 1)
        bridge.discard = () => {
          bridge.edited = new Map()
          setAdded([])
        }
        return resetBridge
        // 마운트 때 한 번 — replaceQuery·menuSession 은 경로가 같으면 같은 값이다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [])

      useEffect(() => {
        // 편집이 남은 행은 편집을 시작한 조회 값을 기준(before)으로 둔다. 그 사이 다른 곳에서 바뀌었으면 저장 때 서버가 거절한다.
        // 재조회 중(새 nonce 라 data 가 잠시 없다)에는 기준을 지우지 않는다.
        if (query.data) {
          const prev = bridge.stored
          bridge.stored = new Map(
            query.data.map((r) => {
              const id = String(r.id)
              return [id, (bridge.edited.has(id) && prev.get(id)) || (r as Row)]
            }),
          )
        }
        bridge.addedCount = added.length
        syncUnsaved()
      }, [added, query.data])

      // 셀 편집을 행 목록에 접어 넣는다. 생성본은 ROWS 참조가 바뀌면(행 추가·업로드) 편집 기준선을 비우고 이 값으로 다시 그린다 — 접지 않으면 편집이 원본으로 돌아간다.
      // 조회·재조회는 그 전에 편집을 비워 서버 값 그대로 그린다. 참조는 added·조회 결과·목록 검색어가 바뀔 때만 바뀐다(생성본 useEffect [ROWS]).
      // 목록 검색 중 표는 걸러진 행만 들고 있어, 검색어가 바뀌면 숨어 있던 행의 편집을 여기서 다시 접어야 원래 값으로 보이지 않는다.
      // foldTick 은 bridge.edited(모듈 변수)를 다시 읽으라는 신호다.
      const rows = useMemo(() => [...added, ...(query.data ?? [])].map((r) => bridge.edited.get(String(r.id)) ?? r), [added, query.data, foldTick])
      return { rows, loading: query.isFetching }
    },
  },
  actions: {
    search: (filters) => {
      const req = toSearchReq(filters.CLS_DATE)
      if (!req) {
        core.message.warning('결제일 기간을 입력해 주세요.')
        return false
      }
      if (req.fromClsDate > req.toClsDate) {
        core.message.warning('결제일 시작일이 종료일보다 늦습니다.')
        return false
      }
      bridge.search(req)
    },
    // 화면관리 프리셋(1.0 COLUMN_PRESET)을 빈 행 위에 얹는다 — 업로드 행에는 쓰지 않는다(1.0 과 같다).
    ma19AddButton: () => {
      bridge.addRows([{ ...blankRow(nextNewId()), ...bridge.slot?.forTable(TABLE_ID).presets }])
      return false
    },
    // 표가 올리는 행은 목록 검색으로 걸러진 행뿐일 수 있다. 통째로 바꾸면 검색에 안 걸린 행의 편집이 사라지므로 받은 행만 갱신한다.
    ROWS_rowsChange: (rows) => {
      rows.forEach((r) => bridge.edited.set(String(r.id), r))
      syncUnsaved()
    },
    // 필수 미입력 안내 — 확인만 닫는다. 저장은 하지 않는다.
    ma19RequiredNotice_ok: () => false,
    ma19SaveConfirm_ok: async (ctx: ActionCtx) => {
      let body
      try {
        body = toSaveRequest(withEdits(ctx.selected), bridge.stored)
      } catch (e) {
        return fail(e, '저장할 값을 확인해 주세요.')
      }
      try {
        await withBusy(() => savePairOrderSchedules(body))
      } catch (e) {
        return fail(e, SAVE_FAILED)
      }
      bridge.removeAdded(new Set(ctx.selected.filter(isNewRow).map((r) => String(r.id))))
      bridge.refetch(new Set(ctx.selected.map((r) => String(r.id))))
    },
    ma19DeleteConfirm_ok: async (ctx: ActionCtx) => {
      const saved = ctx.selected.filter((r) => !isNewRow(r))
      const fresh = ctx.selected.filter(isNewRow)
      try {
        requireWithinSaveCap(saved.length)
      } catch (e) {
        return fail(e, '삭제할 행을 줄여 주세요.')
      }
      if (saved.length) {
        try {
          await withBusy(() => savePairOrderSchedules({ creates: [], updates: [], deleteIds: saved.map((r) => Number(r.id)) }))
        } catch (e) {
          return fail(e, DELETE_FAILED)
        }
      }
      bridge.removeAdded(new Set(fresh.map((r) => String(r.id))))
      if (saved.length) bridge.refetch(new Set(ctx.selected.map((r) => String(r.id))))
    },
    ma19DownloadModal_ok: async (ctx: ActionCtx) => {
      const reason = typeof ctx.form?.reason === 'string' ? ctx.form.reason.trim() : ''
      const detail = typeof ctx.form?.detail === 'string' ? ctx.form.detail.trim() : ''
      if (!reason) return fail(new Error('사유코드를 선택해 주세요.'), '사유코드를 선택해 주세요.')
      if (detail.length < PAIR_ORDER_SCHEDULE_DOWNLOAD_DETAIL_MIN_LENGTH) {
        const message = `상세사유는 ${PAIR_ORDER_SCHEDULE_DOWNLOAD_DETAIL_MIN_LENGTH}글자 이상 입력해 주세요.`
        return fail(new Error(message), message)
      }
      const committed = bridge.committed
      if (!committed) return fail(new Error('먼저 조회해 주세요. 조회한 결제일 기간으로 내려받습니다.'), '먼저 조회해 주세요.')
      try {
        await downloadPairOrderSchedulesExcel({ ...committed, detailedReason: `${reason}_${detail}` })
      } catch (e) {
        return fail(e, '엑셀 다운로드에 실패했습니다.')
      }
      return '엑셀 다운로드를 시작했습니다.'
    },
    ma19UploadModal_ok: async (ctx: ActionCtx) => {
      const file = ctx.form?.file instanceof Blob ? ctx.form.file : undefined
      if (!file) return fail(new Error('업로드할 파일을 선택해 주세요.'), '업로드할 파일을 선택해 주세요.')
      if (file.size > PAIR_ORDER_SCHEDULE_UPLOAD_MAX_BYTES) {
        return fail(new Error(`파일이 너무 큽니다. ${PAIR_ORDER_SCHEDULE_UPLOAD_MAX_MB}MB 이하로 나눠 올려 주세요.`), '파일이 너무 큽니다.')
      }
      let parsed
      try {
        parsed = await parsePairOrderScheduleExcel(file)
      } catch (e) {
        return fail(e, '엑셀 파일을 읽지 못했습니다.')
      }
      if (!parsed.length) return fail(new Error('업로드할 행이 없습니다.'), '업로드할 행이 없습니다.')
      bridge.addRows(parsed.map((values) => ({ ...blankRow(nextNewId()), ...values })))
      return `${parsed.length}건을 표에 추가했습니다. [저장]을 눌러 반영해 주세요.`
    },
  },
  form: {
    ma19DownloadModal: (state) => state,
    ma19UploadModal: (state) => state,
  },
  // 목록 검색어가 바뀌면 숨었던 행의 편집을 다시 접는다(위 ROWS 메모).
  onState: ({ listq }) => {
    const q = listq['table.q'] ?? ''
    if (q === bridge.listQ) return
    bridge.listQ = q
    bridge.fold()
    // 확정한 목록 검색어도 싣는다 — 조회 조건만 남고 검색어가 풀리면 다녀온 뒤 다른 목록으로 보인다.
    if (bridge.committed) bridge.persist(bridge.committed, q)
  },
  // 업로드 뒤 새로 얹은 행만 체크한다 — 조회 행까지 [저장] 대상이 되지 않게.
  selectAllAfterUpload: (row) => isNewRow(row),
  // 버튼 권한 슬롯 — 생성본이 버튼을 숨기고(permCan·permTool) 편집 불가면 셀을 잠근다(permCols). 마운트 전에는 비워 둬 전부 보인다(생성본 기본).
  get permission() {
    return bridge.slot ?? undefined
  },
  // 저장·삭제 요청 중에는 선택 게이트 버튼([저장]·[삭제])을 잠근다.
  useBusy: () => useSyncExternalStore(subscribeBusy, () => busyCount > 0),
  filterDefaults: {
    ma19FilterCard: (demo) => ({ ...demo, CLS_DATE: defaultRange() }),
  },
  // 진입 초기값 — URL 에 남긴 조회 조건이 있으면 그 기간. [초기화]는 filterDefaults(오늘 하루)로 돌아간다.
  filterInitial: {
    ma19FilterCard: (defaults) => {
      const { req } = readSearchQuery()
      return req ? { ...defaults, CLS_DATE: toClsDateRange(req) } : defaults
    },
  },
  // 목록 검색어 복원 — 생성본이 진입 때 한 번 읽는다.
  get stateInitial() {
    return { listq: { 'table.q': readSearchQuery().keyword } }
  },
}
